import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Header from "../../components/public/Header";
import Footer from "../../components/public/Footer";
import TrustedBy from "../../components/public/TrustedBy";
import EnquireDialog from "../../components/public/EnquireDialog";
import { ArrowLeft, Download, ShieldCheck, Pill, FileText, CaretDown } from "@phosphor-icons/react";
import useReveal from "../../hooks/useReveal";
import usePageMeta from "../../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [enquireOpen, setEnquireOpen] = useState(false);

  usePageMeta({
    title: item ? (item.seo?.meta_title || `${item.name} — Bon-Heur Pharma`) : "Product — Bon-Heur Pharma",
    description: item ? (item.seo?.meta_description || item.short_description || item.composition?.split("\n")[0]) : undefined,
    canonical: item ? (item.seo?.canonical_url || undefined) : undefined,
    ogImage: item?.image_url || item?.seo?.og_image,
    ogType: "product",
    keywords: item ? (item.seo?.meta_keywords || `${item.name}, ${item.active_ingredient || ""}, ${item.therapeutic_area || ""}, Bon-Heur pharmaceuticals`).replace(/,\s*,/g, ",") : undefined,
    robots: item?.seo?.robots,
  });

  useEffect(() => {
    axios.get(`${API}/public/products/${slug}`).then((r) => setItem(r.data)).catch((e) => {
      if (e?.response?.status === 404) setNotFound(true);
    });
  }, [slug]);

  // Inject SEO JSON-LD
  useEffect(() => {
    if (!item?.jsonld) return;
    const scripts = item.jsonld.map((blk) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(blk);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => document.head.removeChild(s));
  }, [item]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-[#1F2937]">Product not found</h1>
          <button onClick={() => navigate("/products")} className="mt-6 px-5 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-semibold">Back to products</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20"><div className="h-96 bg-[#F1F5F9] rounded-2xl animate-pulse" /></div>
        <Footer />
      </div>
    );
  }

  const img = item.image_url ? (item.image_url.startsWith("http") ? item.image_url : `${BACKEND}${item.image_url}`) : null;
  const meta = [
    ["Composition", item.composition],
    ["Dosage form", item.dosage_form],
    ["Pack size", item.pack_size],
    ["Active ingredient", item.active_ingredient],
    ["Therapeutic area", item.therapeutic_area],
    ["Manufacturer", item.manufacturer],
    ["Prescription", item.prescription_status?.toUpperCase()],
  ].filter(([, v]) => v);

  const fileUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="product-detail">
      <Header />
      <main>
        <div className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-[#64748B]">
            <Link to="/products" className="inline-flex items-center gap-1.5 hover:text-[#C2410C]"><ArrowLeft size={12} /> All products</Link>
          </div>
        </div>

        <section className="py-12 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div className="relative" style={{ animation: "blurUp 0.7s ease-out both" }}>
              <div className="absolute -top-4 -left-4 w-28 h-28 bg-[#C2410C]/10 rounded-2xl -z-10" aria-hidden />
              <div className="absolute -bottom-4 -right-4 w-28 h-28 bg-[#0EA5E9]/10 rounded-2xl -z-10" aria-hidden />
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#F1F5F9]">
                {img ? (
                  <img src={img} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0EA5E9]/10 to-[#C2410C]/10"><Pill size={80} className="text-[#94A3B8]" /></div>
                )}
              </div>
            </div>

            <div style={{ animation: "blurUp 0.8s ease-out 0.15s both" }}>
              {item.category_name && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C2410C]/10 text-[#C2410C] text-[10px] tracking-[0.3em] uppercase font-semibold mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C2410C]" /> {item.category_name}
                </div>
              )}
              <AtomicDecompHeadline text={item.name} />
              {item.short_description && <p className="mt-4 text-base sm:text-lg text-[#475569] leading-relaxed">{item.short_description}</p>}

              {meta.length > 0 && (
                <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 border border-[#0F172A]/10 rounded-md overflow-hidden" data-testid="product-clinical-grid">
                  {meta.map(([k, v], i) => (
                    <div
                      key={k}
                      className={`group relative p-5 bg-[#F7F8F6] transition-colors duration-200 hover:bg-white ${i % 2 === 0 ? "sm:border-r border-[#0F172A]/10" : ""} ${i < meta.length - 2 ? "border-b border-[#0F172A]/10" : ""}`}
                    >
                      <span className="absolute top-0 left-0 h-0.5 w-0 bg-[#C2410C] transition-all duration-500 group-hover:w-full" />
                      <dt className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C2410C]">{k}</dt>
                      <dd className="mt-2 font-sans text-sm text-[#0F172A] leading-relaxed whitespace-pre-line">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => setEnquireOpen(true)}
                  data-testid="product-enquire-btn"
                  className="btn-fluid inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#0F172A] text-[#0F172A] text-sm font-semibold active:scale-95"
                >
                  Enquire about this product <ShieldCheck size={16} weight="fill" />
                </button>
                {item.stability_report_url && (
                  <a href={fileUrl(item.stability_report_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#E2E8F0] hover:border-[#C2410C] text-[#374151] hover:text-[#C2410C] text-sm font-semibold transition">
                    <FileText size={16} /> Stability report
                  </a>
                )}
                {item.dcgi_approval_url && (
                  <a href={fileUrl(item.dcgi_approval_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#E2E8F0] hover:border-[#C2410C] text-[#374151] hover:text-[#C2410C] text-sm font-semibold transition">
                    <Download size={16} /> DCGI approval
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        {item.description && (
          <DetailBlock title="Product details" content={item.description} />
        )}

        {item.faqs?.length > 0 && (
          <section className="py-14 lg:py-20 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="product-faqs">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Frequently asked questions</h2>
              <div className="mt-8 space-y-3">
                {item.faqs.map((f, i) => <FaqRow key={f.id} f={f} i={i} />)}
              </div>
            </div>
          </section>
        )}
      </main>
      <TrustedBy />
      <Footer />
      <EnquireDialog
        open={enquireOpen}
        onClose={() => setEnquireOpen(false)}
        productName={item?.name}
        source="product-detail"
      />
    </div>
  );
}

function AtomicDecompHeadline({ text }) {
  // Splits the product name into words and animates each with staggered
  // spring physics — evokes molecular parts settling into place.
  if (!text) return null;
  const words = text.split(/\s+/);
  return (
    <h1
      className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#0F172A] leading-[1.05]"
      data-testid="product-headline"
      aria-label={text}
    >
      {words.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 32, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ type: "spring", stiffness: 260, damping: 22, delay: 0.06 * i }}
          className="inline-block mr-[0.25em]"
        >
          {w}
        </motion.span>
      ))}
    </h1>
  );
}

function DetailBlock({ title, content }) {
  const { ref, visible } = useReveal();
  return (
    <section ref={ref} className="py-14 lg:py-20 border-t border-[#E2E8F0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className={`text-2xl sm:text-3xl font-bold text-[#1F2937] transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{ fontFamily: "'Manrope', sans-serif" }}>{title}</h2>
        <div
          className={`prose prose-slate max-w-none mt-6 text-[#374151] leading-relaxed whitespace-pre-line transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "120ms" }}
        >
          {content}
        </div>
      </div>
    </section>
  );
}

function FaqRow({ f, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
        <span className="text-base font-semibold text-[#1F2937]">{f.question}</span>
        <CaretDown size={16} weight="bold" className={`text-[#C2410C] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="px-5 pb-5 text-sm text-[#475569] leading-relaxed whitespace-pre-line">{f.answer}</div>
        </div>
      </div>
    </div>
  );
}
